import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~selectors/SessionInfoSelectors';

import { createExtractsMethod } from '../../../../app/actions/spaces/RunCreateExtractsAction';
import { getAppStore } from '../../../../app/AppStore';
import { AppState } from '../../../../app/state/AppState';
import { Messages } from '../../../../codegen/Localize';
import { ExtractEncryptionState, IDataSourceDetailInfo, IDataSourceInfo } from '../../../../datasource/models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../../../datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '../../../../datasource/resources/Datasources';
import { ExtractEncryptionMode } from '../../../../sites/SitePropertyTypes';
import { IExtractConvertAction } from '../../../../ts/ServerApi/types/actionNames';
import { selectSpaceInfo } from '../../../selectors/SpaceInfoSelectors';
import { isDatasourceSpace, SpaceInfo } from '../../../state/SpaceInfo';
import { EntityActionNames, IEntityAction } from './IEntityAction';
import { extractMoveToTheWebEnabled,
  hasFileBasedDatasource,
  isAnyPendingExtractStatus,
  isEncryptionEnabled,
  isEncryptionEnforced,
  isSingleDatasourceWithEncyptedExtract,
  isSingleDatasourceWithExtract,
  isSingleDatasourceWithUnencyptedExtract,
  isSingleLiveDatasource } from './IEntityActionHelpers';

export const CreateEncryptedExtractForDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
    name: EntityActionNames.CreateExtractsEncrypted,
    translateName: Messages.createExtractsForDsSubActionEncrypted,
    isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
      return CompositeSessionInfoSelectors.isWebExtractionEnabled(state) && isEncryptionEnabled(state);
    },
    isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return isEncryptionEnabled(state) && (isSingleLiveDatasource(targetItems) || isSingleDatasourceWithUnencyptedExtract(targetItems));
    },
    isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return isSingleDatasourceWithExtract(targetItems) && isEncryptionEnabled(state) && isSingleDatasourceWithEncyptedExtract(targetItems);
    },
    createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.encrypted)
};

export const CreateUnencryptedExtractForDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtractsUnencrypted,
  translateName: Messages.createExtractsForDsSubActionUnencrypted,
  isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    return CompositeSessionInfoSelectors.isWebExtractionEnabled(state) && isEncryptionEnabled(state);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return isEncryptionEnabled(state) && (isSingleLiveDatasource(targetItems) || isSingleDatasourceWithEncyptedExtract(targetItems));
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return isSingleDatasourceWithExtract(targetItems) && isEncryptionEnabled(state) && isSingleDatasourceWithUnencyptedExtract(targetItems);
  },
  createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.unencrypted)
};

export const CreateExtractForDsAction: IEntityAction<IDataSourceDetailInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtracts,
  translateName: (targetItems: IDataSourceDetailInfo[], state: AppState) => {
    if (showEncryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionEncrypted();
    } else if (showUnencryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionUnencrypted();
    } else {
      return Messages.createExtractForDsAction();
    }
  },
  isVisible: (targetItems: IDataSourceDetailInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    const spaceInfo: SpaceInfo = selectSpaceInfo(state);
    const isWebExtractionEnabled = CompositeSessionInfoSelectors.isWebExtractionEnabled(state);
    return extractMoveToTheWebEnabled(isWebExtractionEnabled, targetItems) && isDatasourceSpace(spaceInfo);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceDetailInfo[], state: AppState) => {
    return !(availableActions && !availableActions.createExtracts) && isClickableForExtractButton(targetItems, state);
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceDetailInfo[], state: AppState) => {
    return DataSources.isExtract(getCommonDatasourceInfo(targetItems[0]));
  },
  createReduxAction: (targetItems: IDataSourceDetailInfo[]) => createExtractsMethod(targetItems,
    SessionSiteSelectors.extractEncryptionMode(getAppStore().getState()) === ExtractEncryptionMode.ENFORCED
  ? ExtractEncryptionState.encrypted: ExtractEncryptionState.unencrypted)
};

function showUnencryptedButtonName(state: AppState, targetItems: IDataSourceDetailInfo[]) {
  // case 1) Encryption enable, datasource is Unencrypted extract datasource
  return  !isSingleLiveDatasource(targetItems) && (isEncryptionEnabled(state) || isEncryptionEnforced(state)) && isSingleDatasourceWithUnencyptedExtract(targetItems);
}

function showEncryptedButtonName(state: AppState, targetItems: IDataSourceDetailInfo[]) {
  // case 1) datasource is Encrypted extract datasource
  return  isEncryptionEnforced(state) || isSingleDatasourceWithEncyptedExtract(targetItems);
}

function isClickableForExtractButton(targetItems: IDataSourceDetailInfo[], state: AppState) {
 return !hasFileBasedDatasource(targetItems) && !isAnyPendingExtractStatus(targetItems) && (isSingleLiveDatasource(targetItems) || isEncryptionEnabled(state));
}
